import React, { useState } from "react";
import { Gauge, ArrowDown, ArrowUp, Activity, Play } from "lucide-react";
import { PORTAL_BASE } from "./portalApi";
import { usePortalAuth } from "./PortalAuthContext";

const DOWNLOAD_MB = 8;
const UPLOAD_MB = 4;
const HISTORY_KEY = "portal_speedtest_history";

const randomBytes = (mb) => {
  const buf = new Uint8Array(mb * 1024 * 1024);
  for (let i = 0; i < buf.length; i += 65536) crypto.getRandomValues(buf.subarray(i, Math.min(i + 65536, buf.length)));
  return buf;
};

export const PortalSpeedtestPage = () => {
  const { customer } = usePortalAuth();
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [res, setRes] = useState({ ping: null, download: null, upload: null });
  const [history, setHistory] = useState(() => JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"));
  const [error, setError] = useState("");

  const run = async () => {
    setError("");
    setRes({ ping: null, download: null, upload: null });
    try {
      setPhase("ping");
      const pings = [];
      for (let i = 0; i < 5; i++) {
        const s = performance.now();
        await fetch(`${PORTAL_BASE}/speedtest/ping?x=${Math.random()}`, { cache: "no-store" });
        pings.push(performance.now() - s);
      }
      const ping = Math.round(Math.min(...pings.slice(1)));
      setRes((r) => ({ ...r, ping }));

      setPhase("download");
      setProgress(0);
      let s = performance.now();
      const r = await fetch(`${PORTAL_BASE}/speedtest/download?mb=${DOWNLOAD_MB}&x=${Math.random()}`, { cache: "no-store" });
      const reader = r.body.getReader();
      let got = 0;
      const total = DOWNLOAD_MB * 1024 * 1024;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        got += value.length;
        setProgress(Math.round((got / total) * 100));
      }
      const download = +((got * 8) / ((performance.now() - s) / 1000) / 1e6).toFixed(1);
      setRes((r2) => ({ ...r2, download }));

      setPhase("upload");
      setProgress(0);
      const body = randomBytes(UPLOAD_MB);
      s = performance.now();
      await fetch(`${PORTAL_BASE}/speedtest/upload`, { method: "POST", body, headers: { "Content-Type": "application/octet-stream" } });
      const upload = +((body.length * 8) / ((performance.now() - s) / 1000) / 1e6).toFixed(1);
      setProgress(100);
      const final = { ping, download, upload, at: new Date().toISOString() };
      setRes(final);
      const h = [final, ...history].slice(0, 5);
      setHistory(h);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
      setPhase("done");
    } catch (e) {
      setError("Pengujian gagal, periksa koneksi lalu coba lagi");
      setPhase("idle");
    }
  };

  const running = !["idle", "done"].includes(phase);
  const target = customer?.paket?.download_speed;

  return (
    <div className="space-y-4" data-testid="portal-speedtest">
      <h1 className="text-base font-bold text-white flex items-center gap-2"><Gauge className="w-4 h-4 text-emerald-400" /> Speedtest</h1>
      <p className="text-xs text-slate-400">Mengukur kecepatan koneksi Anda ke server billing. Hasil bisa berbeda dari speedtest publik.</p>

      <div className="p-6 rounded-2xl bg-[#0d1527] border border-slate-800 text-center space-y-4">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">{phase === "upload" ? "Upload" : "Download"}</p>
          <p className="text-5xl font-bold text-white font-mono" data-testid="portal-speed-main">
            {phase === "upload" ? (res.upload ?? "—") : (res.download ?? "—")}
          </p>
          <p className="text-xs text-slate-400">Mbps{target ? ` • paket Anda ${target} Mbps` : ""}</p>
        </div>
        {running && (
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all" style={{ width: `${phase === "ping" ? 10 : progress}%` }} /></div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded-xl bg-slate-900"><Activity className="w-4 h-4 text-amber-400 mx-auto" /><p className="text-sm font-bold text-white font-mono mt-1" data-testid="portal-speed-ping">{res.ping ?? "—"}</p><p className="text-[10px] text-slate-500">ms ping</p></div>
          <div className="p-2 rounded-xl bg-slate-900"><ArrowDown className="w-4 h-4 text-cyan-400 mx-auto" /><p className="text-sm font-bold text-white font-mono mt-1" data-testid="portal-speed-download">{res.download ?? "—"}</p><p className="text-[10px] text-slate-500">Mbps down</p></div>
          <div className="p-2 rounded-xl bg-slate-900"><ArrowUp className="w-4 h-4 text-emerald-400 mx-auto" /><p className="text-sm font-bold text-white font-mono mt-1" data-testid="portal-speed-upload">{res.upload ?? "—"}</p><p className="text-[10px] text-slate-500">Mbps up</p></div>
        </div>
        {error && <p className="text-xs text-rose-300" data-testid="portal-speed-error">{error}</p>}
        <button onClick={run} disabled={running} className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="portal-speed-start">
          <Play className="w-4 h-4" /> {running ? `Menguji ${phase}...` : phase === "done" ? "Uji Ulang" : "Mulai Speedtest"}
        </button>
      </div>

      {history.length > 0 && (
        <div className="space-y-1.5" data-testid="portal-speed-history">
          <h2 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Riwayat Terakhir</h2>
          {history.map((h, i) => (
            <div key={i} className="p-2.5 rounded-lg bg-[#0d1527] border border-slate-800 flex items-center justify-between text-xs">
              <span className="text-slate-400">{new Date(h.at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              <span className="font-mono text-white">{h.ping} ms • <span className="text-cyan-300">↓{h.download}</span> • <span className="text-emerald-300">↑{h.upload}</span> Mbps</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
